import { defineStore } from "pinia"
import { ref, inject, computed } from "vue"
import { useFetchWrapper } from "@core/composables"
import type { DataVariable } from "@types"
import {
    BuildingSource,
    CreateSensorPayload,
    Source,
    UpdateSensorPayload,
    BuildingSourcePayload,
} from "@types"
import type { Services } from "@services"
import type { WeatherStationItem } from "@core/services/source.service"
import {
    METER_TYPES_LIST,
    SOURCES_TYPES,
    WEATHER_STATION_TYPES_LIST,
} from "@core/constants"
import { variablesDetenuesElecStrasbourg } from "@core/components/source/meter/display/card/meter-elecstrasbourg.helper"
import { variablesDetenuesUBordeaux } from "@core/components/source/meter/display/card/meter-ubordeaux.helper"
import { useBuildingStore } from "./building-v2.store"

/**
 * Types de variables autorisées pour les graphiques des stations météo (MétéoFrance, OpenMétéo).
 * Quand _variables est vide côté API, on expose cette liste fixe.
 */
const WEATHER_STATION_GRAPH_VARIABLE_TYPES = [
    "dju_air_exterieur",
    "djufroid18_air_exterieur",
    "djufroid24_air_exterieur",
    "humidite_air_exterieur",
    "temperature_air_exterieur",
] as const

/**
 * Store permettant de gérer l'authentification de l'utilisateur au widget
 */
export const useSourceStore = defineStore("source", () => {
    const buildingStore = useBuildingStore()
    // ==================
    // STATE
    // ==================

    /**
     * La liste des sources
     */
    const sources = ref<Source[]>([])

    // ==================
    // GETTERS
    // ==================

    /**
     * Récupère une source par son identifiant
     * @param _id_source L'identifiant de la source
     * @returns La source correspondante ou undefined si non trouvée
     */
    const getSourceById = computed(() => {
        return (_id_source: string): Source | undefined => {
            const source = sources.value.find(
                (source: Source) => source._id_source === _id_source
            )

            // Vérifie si on a un consentrequest dans le bâtiment courant
            if (source) {
                const buildingSource = buildingStore.building?.sources?.find(
                    (s: Source) => s._id_source === source._id_source
                )
                if (buildingSource) {
                    source.consentrequest = buildingSource.consentrequest
                    source.consentement = buildingSource.consentement
                }
            }

            return source
        }
    })

    /**
     * Récupère les informations spécifiques au type de la source (pdl, pce, cpcu, regaz, elecsavoie, dalkia, sigdu)
     * @param _id_source L'identifiant de la source
     * @returns Les informations spécifiques au type de la source ou null si non trouvée
     */
    const getInformationByType = computed(() => {
        return (_id_source: string) => {
            const source = getSourceById.value(_id_source)

            if (!source) return null

            const type = source.type as keyof Source["informations"]

            return source?.informations?.[type] || null
        }
    })

    // ==================
    // ACTIONS ASYNCHRONES
    // ==================

    /**
     * Injection des services
     */
    const services = inject<Services>("services")

    const {
        fetch: fetchSources,
        isFetching: isFetchingSources,
        hasError: hasErrorSources,
        error: errorSources,
    } = useFetchWrapper(
        async (
            {
                _id_sources,
                reset,
                getBuilding,
            }: {
                _id_sources: string[]
                reset?: boolean
                getBuilding?: boolean
            } = {
                _id_sources: [],
                reset: false,
                getBuilding: false,
            }
        ) => {
            if (!_id_sources.length) {
                return
            }

            if (!services?.$getSourcesInformations) {
                throw new Error("Service $getSourcesInformation non disponible")
            }
            if (getBuilding && !services?.$getSourceByIdOldVersion) {
                throw new Error(
                    "Service $getSourceByIdOldVersion non disponible"
                )
            }
            try {
                const _sources =
                    await services.$getSourcesInformations(_id_sources)

                // Récupère tous les legacy en parallèle : une source en erreur
                // n'invalide pas les autres ; échec global uniquement si toutes échouent.
                const legacyResults = await Promise.allSettled(
                    _sources.map((sourceDapi) =>
                        services.$getSourceByIdOldVersion(sourceDapi._id_source)
                    )
                )

                const mergedSources: Source[] = []
                for (let i = 0; i < _sources.length; i++) {
                    const result = legacyResults[i]
                    if (result.status === "fulfilled") {
                        mergedSources.push({
                            ..._sources[i],
                            ...result.value,
                        } as Source)
                    } else {
                        console.error(
                            `Erreur lors de la récupération legacy pour la source ${_sources[i]._id_source}:`,
                            result.reason
                        )
                    }
                }

                if (_sources.length > 0 && mergedSources.length === 0) {
                    const firstRejected = legacyResults.find(
                        (r): r is PromiseRejectedResult =>
                            r.status === "rejected"
                    )
                    throw (
                        firstRejected?.reason ??
                        new Error("Toutes les récupérations legacy ont échoué")
                    )
                }
                if (reset) {
                    sources.value = mergedSources
                } else {
                    // Ajout ou remplacement des sources dans le store
                    mergedSources.forEach((newSource) => {
                        const index = sources.value.findIndex(
                            (source: Source) =>
                                source._id_source === newSource._id_source
                        )
                        if (index !== -1) {
                            // Remplace la source existante
                            sources.value[index] = newSource
                        } else {
                            // Ajoute la nouvelle source
                            sources.value.push(newSource)
                        }
                    })
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération du bâtiment:",
                    error
                )

                if (reset) {
                    sources.value = []
                }

                throw error
            }
        }
    )

    /**
     * Récupère une source via l'API sans mettre à jour le store.
     * Utilisé par les composants qui gèrent eux-mêmes la mise à jour du store (ex. machine GRDF).
     * @param params - Objet contenant _id_source
     * @returns La source fusionnée (DAPI + legacy)
     */
    async function fetchSourceByIdAndReturn(params: {
        _id_source: string
    }): Promise<Source> {
        const { _id_source } = params
        if (!services?.$getSourcesInformations) {
            throw new Error("Service $getSourcesInformations non disponible")
        }
        if (!services?.$getSourceByIdOldVersion) {
            throw new Error("Service $getSourceByIdOldVersion non disponible")
        }
        if (!_id_source) {
            throw new Error("Aucune source sélectionnée pour la récupération")
        }
        const [[sourceDapi], sourceLegacy] = await Promise.all([
            services.$getSourcesInformations([_id_source]),
            services.$getSourceByIdOldVersion(_id_source),
        ])
        return {
            ...sourceDapi,
            ...sourceLegacy,
        } as Source
    }

    /**
     * Insère ou met à jour une source dans le store (sans appel API).
     * @param source - La source à insérer ou mettre à jour
     */
    function upsertSource(source: Source): void {
        const index = sources.value.findIndex(
            (s) => s._id_source === source._id_source
        )
        if (index !== -1) {
            sources.value[index] = source
        } else {
            sources.value.push(source)
        }
    }

    /**
     * Après mise à jour de la station météo côté API (bâtiment déjà à jour dans le store bâtiment) :
     * retire l'ancienne source du store si l'identifiant change, puis fusionne la nouvelle entrée
     * à partir du résumé bâtiment et de l'item liste stations — sans refetch réseau.
     */
    function syncWeatherStationSourceAfterBuildingUpdate(params: {
        oldSourceId: string
        newSourceId: string
        buildingSource: BuildingSource | undefined
        weatherItem: WeatherStationItem
    }): void {
        const { oldSourceId, newSourceId, buildingSource, weatherItem } = params

        if (oldSourceId && oldSourceId !== newSourceId) {
            sources.value = sources.value.filter(
                (s) => s._id_source !== oldSourceId
            )
        }

        const idtype =
            weatherItem.operator === "openmeteo"
                ? "idopenmeteo"
                : "idmeteofrance"

        const existingNew = sources.value.find(
            (s) => s._id_source === newSourceId
        )

        const merged = {
            ...(existingNew ?? {}),
            ...(buildingSource ?? {}),
            _id_source: newSourceId,
            type: "meteo" as const,
            idtype,
            idsource: weatherItem.idsource,
            name:
                weatherItem.name ||
                buildingSource?.name ||
                existingNew?.name ||
                "",
            rights: buildingSource?.rights ?? existingNew?.rights ?? ["read"],
        } as Source

        upsertSource(merged)
    }

    const {
        fetch: fetchSourceByIdRaw,
        isFetching: isFetchingSourceById,
        hasError: hasErrorSourceById,
        error: errorSourceById,
    } = useFetchWrapper(
        async (
            {
                _id_source,
            }: {
                _id_source: string
            } = {
                _id_source: "",
            }
        ) => {
            try {
                const source = await fetchSourceByIdAndReturn({ _id_source })
                upsertSource(source)
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération de la source:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Promesses des fetch source par id en cours (déduplication des appels concurrents par _id_source).
     */
    const fetchSourceByIdPromises = new Map<string, Promise<void>>()

    /**
     * Récupère une source par son identifiant (déduplique les appels simultanés pour le même _id_source).
     * Si un fetch est déjà en cours pour cette source, retourne la même promesse.
     * @param params - Objet contenant _id_source
     */
    async function fetchSourceById(params: {
        _id_source: string
        silent?: boolean
    }): Promise<void> {
        const { _id_source, silent } = params
        if (!_id_source) {
            throw new Error("Aucune source sélectionnée pour la récupération")
        }
        const existing = fetchSourceByIdPromises.get(_id_source)
        if (existing) {
            return existing
        }
        const promise = fetchSourceByIdRaw({ _id_source }, { silent }).finally(
            () => {
                fetchSourceByIdPromises.delete(_id_source)
            }
        )
        fetchSourceByIdPromises.set(_id_source, promise)
        return promise
    }

    // =========================== //
    // Gestion des notes
    // =========================== //

    const {
        fetch: fetchSourceNotes,
        isFetching: isFetchingSourceNotes,
        hasError: hasErrorSourceNotes,
        error: errorSourceNotes,
    } = useFetchWrapper(async (_id_source: string) => {
        if (!services?.$getNotesBySourceId) {
            throw new Error("Service $getNotesBySourceId non disponible")
        }

        if (!_id_source) {
            throw new Error(
                "Aucune source sélectionnée pour récupérer les notes"
            )
        }

        try {
            const notes = await services.$getNotesBySourceId(_id_source)

            // Ajout les notes dans la source correspondante
            const source = sources.value.find(
                (s: Source) => s._id_source === _id_source
            )

            if (source) {
                source.notes = notes || []
            }
        } catch (error) {
            console.error(
                "Erreur lors de la récupération des notes de la source:",
                error
            )
            throw error
        }
    })

    // =========================== //
    // Gestion des demandes de consentement
    // =========================== //

    /**
     * Demande de consentement d'une source, lue du bâtiment courant puis de
     * la liste des sources (même ordre que la suppression)
     * @param _id_source - identifiant de la source
     */
    function findConsentRequestBySourceId(_id_source: string) {
        const fromBuilding = buildingStore.building?.sources.find(
            (s: BuildingSource) => s._id_source === _id_source
        )?.consentrequest
        const fromStore = sources.value.find(
            (s: Source) => s._id_source === _id_source
        )?.consentrequest
        return fromBuilding ?? fromStore
    }

    const {
        fetch: fetchDeleteConsentRequest,
        isFetching: isFetchingDeleteConsentRequest,
        hasError: hasErrorDeleteConsentRequest,
        error: errorDeleteConsentRequest,
    } = useFetchWrapper(async (_id_source: string) => {
        if (!services?.$deleteConsentRequest) {
            throw new Error("Service $deleteConsentRequest non disponible")
        }

        if (!_id_source) {
            throw new Error(
                "Aucune source sélectionnée pour supprimer la demande de consentement"
            )
        }

        try {
            const consentrequest = findConsentRequestBySourceId(_id_source)

            const _id_consentrequest = consentrequest?._id

            if (!_id_consentrequest) {
                throw new Error(
                    "Aucune demande de consentement trouvée pour cette source"
                )
            }

            const typeMapping: Record<
                string,
                "sign" | "share" | "extend" | "change-owner"
            > = {
                SIGN: "sign",
                SHARE: "share",
                EXTEND: "extend",
                CHANGE_OWNER: "change-owner",
            }
            const serviceType =
                typeMapping[consentrequest?.type?.toUpperCase?.() ?? ""] ??
                "sign"

            await services.$deleteConsentRequest(
                _id_consentrequest,
                serviceType
            )

            /** Retire la demande de consentement sur la source (sans supprimer la source). */
            const clearConsentOnSource = (s: Source): Source =>
                ({ ...s, consentrequest: undefined }) as Source

            if (buildingStore.building?.sources?.length) {
                const idx = buildingStore.building.sources.findIndex(
                    (s: Source) => s._id_source === _id_source
                )
                if (idx !== -1) {
                    buildingStore.building.sources.splice(
                        idx,
                        1,
                        clearConsentOnSource(
                            buildingStore.building.sources[idx] as Source
                        ) as BuildingSource
                    )
                }
            }

            const sourceIdx = sources.value.findIndex(
                (s: Source) => s._id_source === _id_source
            )
            if (sourceIdx !== -1) {
                sources.value[sourceIdx] = clearConsentOnSource(
                    sources.value[sourceIdx] as Source
                )
            }
        } catch (error) {
            console.error(
                "Erreur lors de la suppression de la demande de consentement:",
                error
            )
            throw error
        }
    })

    // =========================== //
    // Gestion des capteurs IoT
    // =========================== //

    const {
        fetch: createSensor,
        isFetching: isCreatingSensor,
        hasError: hasErrorCreatingSensor,
        error: errorCreatingSensor,
    } = useFetchWrapper(async (sensorData: CreateSensorPayload) => {
        if (!services?.$createSensor) {
            throw new Error("Service $createSensor non disponible")
        }

        try {
            const newSource = await services.$createSensor(sensorData)

            // Fetch la nouvelle source
            await fetchSourceById({ _id_source: newSource._id_source })
        } catch (error) {
            console.error("Erreur lors de la création du capteur:", error)
            throw error
        }
    })

    const {
        fetch: updateSensor,
        isFetching: isUpdatingSensor,
        hasError: hasErrorUpdatingSensor,
        error: errorUpdatingSensor,
    } = useFetchWrapper(
        async ({
            _id_source,
            sensorData,
        }: {
            _id_source: string
            sensorData: UpdateSensorPayload
        }) => {
            if (!services?.$updateSensor) {
                throw new Error("Service $updateSensor non disponible")
            }

            if (!_id_source) {
                throw new Error(
                    "Aucune source sélectionnée pour la mise à jour"
                )
            }

            try {
                const updatedSource = await services.$updateSensor(
                    _id_source,
                    sensorData
                )

                // Met à jour la source dans le store en fusionnant avec les propriétés existantes
                const index = sources.value.findIndex(
                    (s: Source) => s._id_source === _id_source
                )
                if (index !== -1) {
                    // Fusionne les propriétés existantes avec les nouvelles pour préserver toutes les données
                    sources.value[index] = {
                        ...sources.value[index],
                        ...updatedSource,
                    }
                } else {
                    // Fetch la nouvelle source
                    await fetchSourceById({ _id_source })
                }
            } catch (error) {
                console.error(
                    "Erreur lors de la mise à jour du capteur:",
                    error
                )
                throw error
            }
        }
    )

    // =========================== //
    // Gestion des positions des capteurs IoT
    // =========================== //

    const {
        fetch: createSensorPosition,
        isFetching: isCreatingSensorPosition,
        hasError: hasErrorCreatingSensorPosition,
        error: errorCreatingSensorPosition,
    } = useFetchWrapper(async (sensorPositionData: BuildingSourcePayload) => {
        if (!services?.$addBuildingSource) {
            throw new Error("Service $addBuildingSource non disponible")
        }

        try {
            await services.$addBuildingSource(sensorPositionData)

            // Fetch la source correspondante
            await fetchSourceById({ _id_source: sensorPositionData.sourceId })
        } catch (error) {
            console.error(
                "Erreur lors de la création de la position du capteur:",
                error
            )
            throw error
        }
    })

    const {
        fetch: updateSensorPosition,
        isFetching: isUpdatingSensorPosition,
        hasError: hasErrorUpdatingSensorPosition,
        error: errorUpdatingSensorPosition,
    } = useFetchWrapper(async (sensorPositionData: BuildingSourcePayload) => {
        if (!services?.$updateBuildingSource) {
            throw new Error("Service $updateBuildingSource non disponible")
        }

        try {
            await services.$updateBuildingSource(sensorPositionData)

            updateBuildingFromSource(
                sensorPositionData.buildingId,
                sensorPositionData.sourceId,
                sensorPositionData
            )
        } catch (error) {
            console.error(
                "Erreur lors de la mise à jour de la position du capteur:",
                error
            )
            throw error
        }
    })

    // =========================== //
    // Gestion de la suppression des positions des capteurs IoT
    // =========================== //

    const {
        fetch: deleteSensorPosition,
        isFetching: isDeletingSensorPosition,
        hasError: hasErrorDeletingSensorPosition,
        error: errorDeletingSensorPosition,
    } = useFetchWrapper(
        async ({
            buildingId,
            sourceId,
        }: {
            buildingId: string
            sourceId: string
        }) => {
            if (!services?.$removeBuildingSource) {
                throw new Error("Service $deleteBuildingSource non disponible")
            }

            if (!buildingId || !sourceId) {
                throw new Error(
                    "Aucun bâtiment ou source sélectionné pour la suppression"
                )
            }

            try {
                await services.$removeBuildingSource(buildingId, sourceId)

                // Supprime le bâtiment du champ buildings de la source
                removeBuildingFromSource(buildingId, sourceId)
            } catch (error) {
                console.error(
                    "Erreur lors de la suppression de la position du capteur:",
                    error
                )
                throw error
            }
        }
    )

    /**
     * Crée un fetcher pour la suppression de la position d'un capteur IoT
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @returns Le fetcher pour la suppression de la position d'un capteur IoT
     */
    function getRemoveBuildingFromSourceFetcher(
        buildingId: string,
        sourceId: string
    ) {
        const { fetch, isFetching, hasError, error } = useFetchWrapper(
            async () => {
                if (!services?.$removeBuildingSource) {
                    throw new Error(
                        "Service $deleteBuildingSource non disponible"
                    )
                }

                if (!buildingId || !sourceId) {
                    throw new Error(
                        "Aucun bâtiment ou source sélectionné pour la suppression"
                    )
                }

                // Récupère le bâtiment
                const snapshotBuilding = sources.value
                    .find((s: Source) => s._id_source === sourceId)
                    ?.buildings?.find((b) => b._id === buildingId)

                if (!snapshotBuilding) {
                    throw new Error("Aucun bâtiment trouvé pour la suppression")
                }

                try {
                    // Supprime le bâtiment du champ buildings de la source
                    removeBuildingFromSource(buildingId, sourceId)

                    await services.$removeBuildingSource(buildingId, sourceId)
                } catch (error) {
                    console.error(
                        "Erreur lors de la suppression de la position du capteur:",
                        error
                    )

                    // Restaure le bâtiment dans le champ buildings de la source
                    updateBuildingFromSource(snapshotBuilding._id, sourceId, {
                        buildingId: snapshotBuilding._id,
                        sourceId,
                        start_date: snapshotBuilding.start_date || "",
                        end_date: snapshotBuilding.end_date || "",
                        description: snapshotBuilding.description,
                        floor: snapshotBuilding.floor || "",
                    })
                    throw error
                }
            }
        )
        return {
            fetch,
            isFetching,
            hasError,
            error,
        }
    }

    // =========================== //
    // Fonction utiliare pour la gestion des bâtiments des sources
    // =========================== //

    /**
     * Réinitialise le store à son état initial.
     */
    function $reset() {
        sources.value = []
    }

    /**
     * Supprime un bâtiment d'une source
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @returns void
     */
    function removeBuildingFromSource(
        buildingId: string,
        sourceId: string
    ): void {
        const idx = sources.value.findIndex(
            (s: Source) => s._id_source === sourceId
        )
        if (idx === -1) return
        const src = sources.value[idx]
        if (!src.buildings) return
        // Utilise splice pour garantir la réactivité de la suppression du bâtiment
        const buildingIdx = src.buildings.findIndex((b) => b._id === buildingId)
        if (buildingIdx !== -1) {
            src.buildings.splice(buildingIdx, 1)
        }
    }

    /**
     * Fonction permettant de mettre à jour le bâtiment d'une source
     * @param buildingId - Identifiant du bâtiment
     * @param sourceId - Identifiant de la source
     * @param payload - Payload de mise à jour du bâtiment
     * @returns void
     */
    function updateBuildingFromSource(
        buildingId: string,
        sourceId: string,
        payload: BuildingSourcePayload
    ): void {
        // Récupère l'index de la source dans le store
        const idx = sources.value.findIndex(
            (s: Source) => s._id_source === sourceId
        )
        if (idx === -1) return

        // Met à jour le champ buildings de la source

        if (!sources.value[idx].buildings) {
            sources.value[idx].buildings = []
        }

        // Met à jour le bâtiment dans le champ buildings de la source (plus réactif)
        const buildings = sources.value[idx].buildings
        const buildingIdx = buildings.findIndex((b) => b._id === buildingId)
        if (buildingIdx !== -1) {
            // Utilise Vue.set style (implémenté par splice pour la réactivité)
            buildings.splice(buildingIdx, 1, {
                ...buildings[buildingIdx],
                ...payload,
            })
        }
    }

    // =========================== //
    // Variables disponibles par source
    // =========================== //

    /**
     * Récupère les types de variables disponibles pour une source (par son id).
     * Pour les stations météo, si _variables est vide, retourne la liste fixe autorisée.
     * @param _id_source - Identifiant de la source
     * @returns Liste des types de variables (string[])
     */
    function getVariablesBySourceId(_id_source: string): string[] {
        const source = getSourceById.value(_id_source)
        if (!source) return []

        let type = source.type
        if (![...METER_TYPES_LIST, "meteo"].includes(type)) {
            type = "iot"
        }

        const informations = source.informations as Record<string, unknown>

        // Strasbourg Électricité Réseaux : dapi sert `_variables: null` et les
        // bornes sous `data.<variable>.<pas>` (DEV-421).
        if (type === SOURCES_TYPES.ELECSTRASBOURG) {
            return variablesDetenuesElecStrasbourg(
                source.informations?.elecstrasbourg
            )
        }

        // Université de Bordeaux : la variable du fluide et son index, lus dans
        // `_variables` ou, à défaut, dans les bornes de `data` (DEV-2100).
        if (type === SOURCES_TYPES.UBORDEAUX) {
            return variablesDetenuesUBordeaux(source.informations?.ubordeaux)
        }

        const variables =
            (informations[type] as { data?: { _variables?: DataVariable[] } })
                ?.data?._variables ?? ([] as DataVariable[])

        if (
            WEATHER_STATION_TYPES_LIST.includes(source.type) &&
            (!variables || variables.length === 0)
        ) {
            return [...WEATHER_STATION_GRAPH_VARIABLE_TYPES]
        }

        return variables.map((v) => v.type)
    }

    return {
        // State
        sources,

        // Getters
        getSourceById,
        getInformationByType,

        // Reset
        $reset,

        // Actions asynchrones
        fetchSources,
        fetchSourceById,
        fetchSourceByIdAndReturn,
        upsertSource,
        syncWeatherStationSourceAfterBuildingUpdate,
        fetchSourceNotes,
        fetchDeleteConsentRequest,
        findConsentRequestBySourceId,
        createSensor,
        updateSensor,
        createSensorPosition,
        updateSensorPosition,
        deleteSensorPosition,
        // États de chargement
        isFetchingSources,
        isFetchingDeleteConsentRequest,
        isFetchingSourceById,
        isFetchingSourceNotes,
        isCreatingSensor,
        isUpdatingSensor,
        isCreatingSensorPosition,
        isUpdatingSensorPosition,
        isDeletingSensorPosition,

        // États d'erreur
        hasErrorSources,
        hasErrorDeleteConsentRequest,
        hasErrorSourceById,
        hasErrorSourceNotes,
        hasErrorCreatingSensor,
        hasErrorUpdatingSensor,
        hasErrorCreatingSensorPosition,
        hasErrorUpdatingSensorPosition,
        hasErrorDeletingSensorPosition,
        // Erreurs
        errorSources,
        errorSourceById,
        errorSourceNotes,
        errorDeleteConsentRequest,
        errorCreatingSensor,
        errorUpdatingSensor,
        errorCreatingSensorPosition,
        errorUpdatingSensorPosition,
        errorDeletingSensorPosition,

        // Fonctions
        getVariablesBySourceId,
        getRemoveBuildingFromSourceFetcher,
        removeBuildingFromSource,
    }
})
